'use client';

import { Button, LinkButton, Panel } from '@/ui';

interface RenderErrorViewProps {
  title: string;
  message: string;
  /** Next's server-error digest — the only handle support has on a prod stack. */
  digest?: string;
  onRetry: () => void;
  /** Omitted where every in-app destination would be a login wall. */
  home?: { href: string; label: string };
  /**
   * Leave the app instead of soft-navigating. global-error passes this: after a
   * chunk failure a next/link nav just re-enters the same missing chunk, and
   * that boundary has no live call to preserve anyway.
   */
  hardNav?: boolean;
}

/**
 * The surface every error boundary renders. Pure props → markup: the
 * boundaries own the reporting side effect, so this stays renderable anywhere,
 * including inside global-error where the app's providers are gone.
 */
export function RenderErrorView({
  title,
  message,
  digest,
  onRetry,
  home,
  hardNav = false,
}: RenderErrorViewProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950 px-6 py-10">
      <Panel title={title} className="w-full max-w-md">
        <p className="text-sm text-zinc-300">{message}</p>
        {digest ? (
          <p className="mt-2 text-xs text-zinc-400 tabular-nums">Reference {digest}</p>
        ) : null}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button variant="primary" onClick={onRetry}>
            Try again
          </Button>
          {home ? (
            <LinkButton href={home.href} hardNav={hardNav}>
              {home.label}
            </LinkButton>
          ) : null}
        </div>
      </Panel>
    </div>
  );
}
